<template>
    <div
        class="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
        :class="[textClass, mb]"
    >
        <component :is="icon" v-if="icon" :size="14" :class="iconClass" v-bind="iconAttrs" />
        <slot>{{ label }}</slot>
    </div>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';

type EyebrowTone = 'primary' | 'warning' | 'success' | 'error' | 'muted' | 'inverse';

const props = withDefaults(
    defineProps<{
        icon?: Component | null;
        iconFill?: boolean;
        label?: string;
        tone?: EyebrowTone;
        mb?: string;
    }>(),
    {
        icon: null,
        iconFill: false,
        label: '',
        tone: 'primary',
        mb: 'mb-3',
    },
);

// Some hues (warning #fcb700, error #ff627d, success at small sizes) fail
// WCAG 2.1 AA contrast on light backgrounds for text — but they're fine for
// thicker strokes / decorative shapes like the leading icon. So for those
// hues we keep the text neutral and tint only the icon. `primary`, `muted`,
// `inverse` already meet contrast and stay tinted top-to-bottom.
const HUE_TONES = new Set<EyebrowTone>(['warning', 'error', 'success']);

const ICON_TONE_CLASSES: Record<EyebrowTone, string> = {
    primary: 'text-primary',
    warning: 'text-warning',
    success: 'text-success',
    error: 'text-error',
    muted: 'text-base-content/70',
    inverse: 'text-primary-content/80',
};

const textClass = computed(() => {
    if (HUE_TONES.has(props.tone)) {
        return 'text-base-content';
    }

    return ICON_TONE_CLASSES[props.tone];
});

const iconClass = computed(() => ICON_TONE_CLASSES[props.tone]);

const iconAttrs = computed(() => (props.iconFill ? { weight: 'fill' } : {}));
</script>
